import { Link, useLocation, useParams, useRouterState } from '@tanstack/react-router'
import { FR, GB } from 'country-flag-icons/string/3x2'
import { ChevronDown } from 'lucide-react'
import { useTranslations } from 'use-intl'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LOCALES, LOCALE_META, type Locale } from '@/i18n/config'
import { cn } from '@/lib/utils'

// SVG strings, not `country-flag-icons/react/3x2`: that entry defines all ~250
// flags in one module, while the string entry re-exports one file per flag, so
// only the flags listed here ship with the header. A locale whose country is
// missing here fails `pnpm lint`.
const FLAGS: Record<(typeof LOCALE_META)[Locale]['country'], string> = { FR, GB }

function Flag({ locale }: { locale: Locale }) {
  return (
    <img
      src={`data:image/svg+xml,${encodeURIComponent(FLAGS[LOCALE_META[locale].country])}`}
      alt=""
      className="h-4 w-6 shrink-0 rounded-[2px] object-cover"
    />
  )
}

type HeadLink = { rel?: string; hrefLang?: string; href?: string }

// Prefer the page's hreflang alternate (blog/legal slugs differ per locale);
// otherwise swap the locale segment. A page with alternates but none for the
// target (untranslated post) falls back to the target's home.
function useLocaleHref(target: Locale) {
  const { pathname } = useLocation()
  const links = useRouterState({
    select: (s) => s.matches.flatMap((m) => (m.links as HeadLink[] | undefined) ?? []),
  })
  const alternates = links.filter((l) => l.rel === 'alternate')
  const alt = alternates.find((l) => l.hrefLang === target)?.href
  if (alt) return new URL(alt).pathname
  if (alternates.length) return `/${target}`
  return pathname.replace(/^\/[^/]+/, `/${target}`)
}

function LocaleItem({ target }: { target: Locale }) {
  const href = useLocaleHref(target)
  return (
    <DropdownMenuItem asChild>
      <Link to={href as never} hrefLang={target} lang={target}>
        <Flag locale={target} />
        {LOCALE_META[target].name}
      </Link>
    </DropdownMenuItem>
  )
}

export function LocaleSwitcher({ className }: { className?: string }) {
  const t = useTranslations()
  const { locale } = useParams({ from: '/$locale' }) as { locale: Locale }
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        aria-label={t('nav.language')}
        className={cn(
          'flex items-center gap-1.5 rounded-md px-2 py-1.5 transition-colors hover:bg-primary/10',
          className,
        )}
      >
        <Flag locale={locale} />
        <ChevronDown className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LOCALES.filter((l) => l !== locale).map((l) => (
          <LocaleItem key={l} target={l} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
